<template>
  <div
    v-if="showDebug"
    class="auto-ads-debug"
  >
    <div class="debug-header">
      <h4>🤖 Auto Ads Debug</h4>
      <button
        class="close-btn"
        @click="closeDebug"
      >
        ×
      </button>
    </div>

    <div class="debug-content">
      <div class="status-item">
        <strong>Status:</strong>
        <span :class="statusClass">{{ status.autoAdsStatus }}</span>
      </div>

      <div class="status-item">
        <strong>Auto Ads Enabled:</strong>
        <span :class="status.isAutoAdsEnabled ? 'status-success' : 'status-error'">
          {{ status.isAutoAdsEnabled ? '✅' : '❌' }}
        </span>
      </div>

      <div class="status-item">
        <strong>AdSense Loaded:</strong>
        <span :class="status.isAdSenseLoaded ? 'status-success' : 'status-error'">
          {{ status.isAdSenseLoaded ? '✅' : '❌' }}
        </span>
      </div>

      <div class="status-item">
        <strong>Queue Length:</strong> {{ status.adSenseQueue }}
      </div>

      <div class="status-item">
        <strong>DOM AdSense Elements:</strong> {{ domStats.adsenseElements }}
      </div>

      <div class="status-item">
        <strong>Rendered Ads:</strong> {{ domStats.renderedAds }}
      </div>

      <div
        v-if="status.lastError"
        class="status-item error"
      >
        <strong>Last Error:</strong> {{ status.lastError.message }}
      </div>

      <div class="debug-actions">
        <button
          class="btn btn-primary"
          @click="refreshStatus"
        >
          🔄 Refresh
        </button>
        <button
          class="btn btn-secondary"
          @click="checkStatus"
        >
          🔍 Check Status
        </button>
        <button
          class="btn btn-info"
          @click="enableDebugMode"
        >
          🐛 Enable Debug
        </button>
      </div>

      <div class="tips">
        <h5>💡 Tips for Auto Ads:</h5>
        <ul>
          <li>Auto ads may not show immediately - they can take minutes or hours</li>
          <li>Low traffic sites may see fewer auto ads</li>
          <li>Ad blockers will prevent auto ads from showing</li>
          <li>Google needs to review your content before showing auto ads</li>
          <li>Auto ads appear as: anchors, vignettes, in-content, sidebars</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import adSenseAutoAds from '../utils/ads';

export default {
  name: 'AutoAdsDebug',
  data() {
    return {
      showDebug: false,
      status: {},
      domStats: {
        adsenseElements: 0,
        renderedAds: 0
      }
    };
  },
  computed: {
    statusClass() {
      switch (this.status.autoAdsStatus) {
        case 'enabled': return 'status-success';
        case 'error':
        case 'timeout': return 'status-error';
        case 'initializing': return 'status-warning';
        default: return 'status-default';
      }
    }
  },
  mounted() {
    // Expose method globally for easy access
    window.showAutoAdsDebug = () => {
      this.showDebugPanel();
    };

    // Auto-show in development mode or when ?debug=ads is in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (process.env.NODE_ENV === 'development' || urlParams.get('debug') === 'ads') {
      setTimeout(() => {
        this.showDebugPanel();
      }, 2000);
    }
  },
  methods: {
    showDebugPanel() {
      this.showDebug = true;
      this.refreshStatus();
    },
    closeDebug() {
      this.showDebug = false;
    },
    refreshStatus() {
      this.status = adSenseAutoAds.getStatus();
      this.updateDOMStats();
    },
    checkStatus() {
      adSenseAutoAds.checkAutoAdsStatus();
      this.refreshStatus();
    },
    enableDebugMode() {
      adSenseAutoAds.setDebugMode(true);
      console.log('Debug mode enabled. Check console for detailed logs.');
    },
    updateDOMStats() {
      if (typeof document !== 'undefined') {
        const adsenseElements = document.querySelectorAll('ins.adsbygoogle');
        let renderedAds = 0;

        adsenseElements.forEach(el => {
          if (el.innerHTML.trim() !== '') {
            renderedAds++;
          }
        });

        this.domStats = {
          adsenseElements: adsenseElements.length,
          renderedAds: renderedAds
        };
      }
    }
  }
};
</script>

<style scoped>
.auto-ads-debug {
  position: fixed;
  top: 20px;
  right: 20px;
  background: #1a1a1a;
  color: #fff;
  padding: 20px;
  border-radius: 10px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  z-index: 10000;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  border: 2px solid #333;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  border-bottom: 1px solid #444;
  padding-bottom: 10px;
}

.debug-header h4 {
  margin: 0;
  color: #4CAF50;
}

.close-btn {
  background: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
}

.status-item {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-success { color: #4CAF50; }
.status-error { color: #f44336; }
.status-warning { color: #ff9800; }
.status-default { color: #fff; }

.error {
  color: #f44336;
  font-size: 12px;
}

.debug-actions {
  margin: 15px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
}

.btn-primary { background: #2196F3; color: white; }
.btn-secondary { background: #757575; color: white; }
.btn-info { background: #00BCD4; color: white; }

.tips {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #444;
  font-size: 12px;
}

.tips h5 {
  margin: 0 0 10px 0;
  color: #ff9800;
}

.tips ul {
  margin: 0;
  padding-left: 15px;
}

.tips li {
  margin-bottom: 5px;
  line-height: 1.4;
}
</style>
